import { Button, Backdrop, Grid, alpha } from "@mui/material";
import { Navigate } from "react-router-dom";
import Lottie from "lottie-react";
import FileTransfer_Animation from "../animations/FileTransfer_Animation.json";
import { makeStyles } from "@mui/styles";

/**
 * getDateFormat
 *
 * @summary Formats a date string into "DD/MM/YYYY" format.
 *
 * @description
 * Converts the provided date string into a JavaScript Date object and returns a string formatted as "day/month/year". Returns an empty string if the input is null.
 *
 * @param {string|null} strdate - The date string to format.
 *
 * @returns {string} The formatted date string in "DD/MM/YYYY" format.
 *
 * @example
 * const formatted = getDateFormat("2024-06-01T12:00:00Z"); // "01/06/2024"
 */
export const getDateFormat = (strdate) => {
  var db_date = "";
  if (strdate !== null) {
    var tdate = new Date(strdate);
    const day = ("0" + tdate.getDate()).slice(-2);
    const month = ("0" + (tdate.getMonth() + 1)).slice(-2);
    db_date = day + "/" + month + "/" + tdate.getFullYear();
  }
  return db_date;
};

/**
 * getDateTimeFormat
 *
 * @summary Formats a date string into "DD/MM/YYYY HH:mm" format.
 *
 * @description
 * Converts the provided date string into a JavaScript Date object and returns a string formatted as "day/month/year hours:minutes". Returns an empty string if the input is null.
 *
 * @param {string|null} strdate - The date string to format.
 *
 * @returns {string} The formatted date and time string in "DD/MM/YYYY HH:mm" format.
 *
 * @example
 * const formatted = getDateTimeFormat("2024-06-01T12:34:00Z"); // "01/06/2024 12:34"
 */
export const getDateTimeFormat = (strdate) => {
  let db_date = "";
  if (strdate !== null) {
    const tdate = new Date(strdate);
    const day = ("0" + tdate.getDate()).slice(-2);
    const month = ("0" + (tdate.getMonth() + 1)).slice(-2);
    const year = tdate.getFullYear();
    const hours = ("0" + tdate.getHours()).slice(-2);
    const minutes = ("0" + tdate.getMinutes()).slice(-2);

    db_date = `${day}/${month}/${year} ${hours}:${minutes}`;
  }
  return db_date;
};

/**
 * getDateFormatUS
 *
 * @summary Formats a date string into "YYYY/MM/DD" format.
 *
 * @description
 * Converts the provided date string into a JavaScript Date object and returns a string formatted as "year/month/day". Returns an empty string if the input is null.
 *
 * @param {string|null} strdate - The date string to format.
 *
 * @returns {string} The formatted date string in "YYYY/MM/DD" format.
 *
 * @example
 * const formatted = getDateFormatUS("2024-06-01T12:00:00Z"); // "2024/06/01"
 */
export const getDateFormatUS = (strdate) => {
  var db_date = "";
  if (strdate !== null) {
    var tdate = new Date(strdate);
    const day = ("0" + tdate.getDate()).slice(-2);
    const month = ("0" + (tdate.getMonth() + 1)).slice(-2);
    db_date = tdate.getFullYear() + "/" + month + "/" + day;
  }
  return db_date;
};

export const AutocompleteX = {
  display: "inline-block",
  width: "100%",
};

export const Mandatory = () => {
  return <span className="mandatoryStar">*</span>;
};

export const SubmitSuccess = (redirect, pathname, ButtonName) => {
  return redirect ? (
    <Navigate to={pathname} />
  ) : (
    <Grid item>
      <Button variant="contained" disabled>
        {" "}
        {ButtonName}{" "}
      </Button>
      <Backdrop 
        open={true} 
        sx={{
            ...BackdropStyles(),
              zIndex: (theme) => theme.zIndex.modal - 1,
            }}
      >
        {/* <CircularProgress color="inherit" sx={CircularProgressStyles()}/> */}
        <Lottie
          animationData={FileTransfer_Animation}
          loop
          autoplay
          style={{ width: 250, height: 250 }}
        />
      </Backdrop>
    </Grid>
  );
};

export const waitingTime = () => {
  return 4000;
};

/**
 * errorHandling
 *
 * @summary Handles common HTTP error responses and network issues, updating the error state with user-friendly messages.
 *
 * @description
 * Checks the HTTP response status and sets an appropriate error message for 401, 404, 500, and 400 errors. Also detects offline status and sets a network error message. Intended for use in UI components to display error feedback to users.
 *
 * @param {Object} response - The HTTP response object containing the status code.
 * @param {Function} setError - Function to update the error state or display error messages.
 *
 * @returns {void}
 *
 * @example
 * errorHandling(response, setError);
 */
export function errorHandling(response, setError) {
  if (response) {
    switch (response.status) {
      case 400:
        setError(
          <span className="err_color">
            Invalid request. Please verify your input and try again.
          </span>
        );
        break;

      case 401:
        setError(
          <span className="err_color">
            Session expired or unauthorized access. Please log in again.
          </span>
        );
        break;

      case 404:
        setError(
          <span className="err_color">
            Requested resource not found. Please check and try again.
          </span>
        );
        break;

      case 500:
        setError(
          <span className="err_color">
            Server encountered an error. Please contact support or try again later.
          </span>
        );
        break;

      default:
        setError(
          <span className="err_color">
            Something went wrong. Please try again later.
          </span>
        );
        break;
    }
  }

  if (!navigator.onLine) {
    setError(
      <span className="err_color">
        No internet connection detected. Please check your network and try again.
      </span>
    );
  }
}

export function ButtonStyles(theme) {
  return {
    background: "#129EA3",
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: "500",
    borderRadius: "8px",
    border: "none",
    alignItems: "center",
    boxShadow: `0 4px 20px ${alpha("#558b9aff", 0.2)}`,
    letterSpacing: "1px",
    cursor: "pointer",
    transition: "transform 0.2s ease-in-out",
    margin: "5px",
    gap: "10px",
    bottom: "5px",
  };
}

export function UploadButton() {
  return {
    background: "#129EA3",
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: 500,
    borderRadius: "10px",
    boxShadow: `0 4px 20px ${alpha("#558b9aff", 0.2)}`,
    letterSpacing: "1px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    top: "1px",
    bottom: "5px",
    "&:hover": {
      backgroundColor: "#91887e",
    },
  };
}

export function ListItemsStyles(theme) {
  return {
    padding: "0px 10px",
    marginTop: "5px",
    borderRadius: "10px",
      background: theme.palette.themeMode == "dark" ? "#2c2b4c82" : "#87cefa",

    [theme.breakpoints.down("sm")]: {
      padding: "0px 6px",
      marginTop: "3px",
      borderRadius: "6px",
      fontSize: "12px",
    },
    [theme.breakpoints.up("md")]: {
      padding: "0px 12px",
      marginTop: "6px",
      borderRadius: "10px",
      fontSize: "14px",
    },
    [theme.breakpoints.up("lg")]: {
      padding: "0px 16px",
      marginTop: "8px",
      borderRadius: "12px",
      fontSize: "15px",
    },
  };
}

export function BackdropStyles() {
  return {
    zIndex: 9999,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  };
}

export function CircularProgressStyles() {
  return {
    zIndex: 10000,
    color: "#314BCA",
  };
}

export function ReportTableStyles(theme) {
  return {
    border: "1px solid grey",
    borderRadius: "5px",
    backgroundColor:
      theme.palette.themeMode === "dark"
        ? theme.palette.primary.light
        : "#fff",
    overflowY: "auto",
    flexGrow: 1,
    position: "relative",

    maxHeight: "calc(100vh - 220px)",

    [theme.breakpoints.down("sm")]: {
      maxHeight: "calc(100vh - 160px)",
      borderRadius: "3px",
    },

    [theme.breakpoints.up("md")]: {
      maxHeight: "calc(100vh - 180px)",
    },

    [theme.breakpoints.up("lg")]: {
      maxHeight: "calc(100vh - 140px)",
    },

    [theme.breakpoints.up("xl")]: {
      maxHeight: "calc(100vh - 100px)",
    },

    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#888",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#555",
    },
  };
}

export function TableHeaderStyles(theme) {
  return {
    textAlign: "center",
    fontSize: "17px",
    width: "auto",
    fontWeight: "600",
    backgroundColor:
      theme.palette.themeMode == "dark"
        ? "#1D2029"
        : "#1976D2",
    color:
      theme.palette.themeMode == "dark"
        ? "#FDFEFA"
        : "rgba(0, 0, 0, 0.5)",
    border: "1px solid #575757",

    [theme.breakpoints.down("sm")]: {
      fontSize: "13px",
      padding: "4px",
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "15px",
      padding: "6px",
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "18px",
      padding: "8px",
    },
  };
}

export function DialogStyles(theme) {
  return {
    "& .MuiDialog-paper": {
      width: "100%",
      borderRadius: "8px",
      backgroundColor: theme.palette.themeMode == "dark" ? "#353551" : "#fff",

      [theme.breakpoints.down("sm")]: {
        width: "95%",
        borderRadius: "6px",
      },
      [theme.breakpoints.up("md")]: {
        width: "80%",
      },
      [theme.breakpoints.up("lg")]: {
        width: "60%",
      },
    },
  };
}

/**
 * toYearFormat
 *
 * @summary Formats a date string into "YYYY-M-D" format.
 *
 * @description
 * Converts the provided date string into a JavaScript Date object and returns a string formatted as "year-month-day" (without leading zeros). Returns an empty string if the input is null.
 *
 * @param {string|null} strdate - The date string to format.
 *
 * @returns {string} The formatted date string in "YYYY-M-D" format.
 *
 * @example
 * const formatted = toYearFormat("2024-06-01T12:00:00Z"); // "2024-6-1"
 */
export const toYearFormat = ((strdate) => {
  var db_date = ''
  if (strdate !== null){
      var tdate = new Date(strdate);
        db_date = tdate.getFullYear()+'-'+(tdate.getMonth()+1)+'-'+tdate.getDate();
  }
  return db_date
});

/**
 * normalizeFilename
 *
 * @summary Normalizes a filename or URL string for safe storage and usage.
 *
 * @description
 * Extracts the filename from a path or URL, converts it to lowercase, trims whitespace, replaces spaces with underscores, removes invalid characters, and ensures the extension is preserved. Returns the cleaned filename string.
 *
 * @param {string} nameOrUrl - The filename or URL string to normalize.
 *
 * @returns {string} The normalized filename string.
 *
 * @example
 * const safeName = normalizeFilename("My File (1).PDF"); // "my_file_1.pdf"
 */
export const normalizeFilename = (nameOrUrl) => {
  if (!nameOrUrl || typeof nameOrUrl !== "string") return "";

  let name = nameOrUrl.includes("/") ? nameOrUrl.split("/").pop() : nameOrUrl;

  name = name.toLowerCase().trim();

  const parts = name.split(".");
  let ext = parts.length > 1 ? "." + parts.pop().toLowerCase() : "";
  let base = parts.join(".");

  base = base
    .replace(/\s+/g, "_")
    .replace(/[^A-Za-z0-9._-]/g, "")
    .replace(/^\.+/, "")

  if (ext && base.endsWith(ext)) {
    base = base.slice(0, -ext.length);
  }

  return base + ext;
};

/**
 * handleApiError
 *
 * @summary Handles API errors by setting user-friendly error messages and dialog state.
 *
 * @description
 * Checks the error object for network, timeout, and HTTP status errors. Sets appropriate error messages and types using the provided state setters. Opens the error dialog for user feedback. Returns a retry flag for connection timeouts.
 *
 * @param {Object} error - The error object from the API request.
 * @param {Function} setErrorMessage - Function to set the error message state.
 * @param {Function} setErrorDialog - Function to open or close the error dialog.
 * @param {Function} setErrorType - Function to set the error type (e.g., "network", "server").
 *
 * @returns {Object|void} Returns { retry: true } for connection timeouts, otherwise void.
 *
 * @example
 * handleApiError(error, setErrorMessage, setErrorDialog, setErrorType);
 */
export function handleApiError(error, setErrorMessage, setErrorDialog, setErrorType) {
  console.error("API Error:", error);

  if (error.code === "ECONNABORTED") {
    return { retry: true };
  }

  if (!error.response) {
    if (!navigator.onLine) {
      setErrorMessage("Network connection lost. Please check your internet.");
      setErrorType("network");
    } else {
      setErrorMessage("Unable to reach the server at the moment. Please try again shortly or contact the administrator.");
      setErrorType("server");
    }
    setErrorDialog(true);
    return;
  }

  if (error.response.status >= 500) {
    setErrorMessage("The server encountered an issue while processing your request. Please try again or contact the administrator.");
    setErrorType("server");
    setErrorDialog(true);
    return;
  }

  setErrorMessage("Request failed due to a server issue. Please try again or contact the administrator.");
  setErrorType("server");
  setErrorDialog(true);
}

export const useIconMenuStyles = makeStyles((theme) => ({
  iconMenu: {
    position: "fixed",
    top: "50px",
    left: 0,
    height: "auto",
    width: "220px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 10,
    padding: "0px 4px",
    zIndex: 1000,
    overflowY: "auto",
    maxHeight: "calc(100vh - 50px)",
  },

  iconItem: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    textAlign: "left",
    color: "#FCFCFC",
    textDecoration: "none",
    padding: "5px",
    width: "100%",
    gap: "10px",
    borderRadius: "8px",
    transition: "all 0.3s",
    fontFamily: "IBM Plex Sans, sans-serif",
    "&:hover": {
      backgroundColor:
        theme.palette.themeMode === "dark" ? "#91887e" : "#87cefa",
      transform: "translateX(4px)",
    },
  },

  iconImage: {
    borderRadius: "5%",
    backgroundSize: "cover",
    backgroundPosition: "center",
    flexShrink: 0,
  },

  "@media (min-width: 1028px)": {
    iconMenu: {
      overflowX: "hidden",
      justifyContent: "flex-start",
    },
    iconItem: {
      width: "100%",
    },
  },
}));

export const useMainLayoutStyles = makeStyles((theme) => ({
    grow: {
        flexGrow: 1
    },
    headerAvatar: {
        ...theme.typography.commonAvatar,
        ...theme.typography.mediumAvatar,
        transition: 'all .2s ease-in-out',
    },
    iconButton: {
        backgroundColor: '#216bfd',
        color: 'white',
        '&:hover': {
            background: theme.palette.secondary.dark,
        },
      },
    disp : {
        bgcolor:'primary.light'
    },
    root: {
        display: 'flex',
        justifyContent: 'flex-end',
        width: '100%',
    },
}));



